import * as React from 'react';
import {
    CreativeClickProps,
    logCreativeClick,
    logCreativeLoad,
    logVideoLoad,
} from '../../utils/logger';
import { TImprovedContentMarketing } from '../../../shared/types/contentMarketing';
import { useEffect, useMemo, useState } from 'react';
import { getConfig } from '../../config';
import SponsorHeader from './components/sponsor-header';

type TUsePulseLoadAndClick = {
    placementId: string;
    contentMarketing: TImprovedContentMarketing;
    isMultipleAds: boolean;
    multiAdIndex: number;
};

const usePulseLoadAndClick = ({
    placementId: originalPlacementId,
    contentMarketing,
    isMultipleAds,
    multiAdIndex,
}: TUsePulseLoadAndClick): CreativeClickProps => {
    const [pulseInitiated, setPulseInitiated] = useState(false);
    const config = getConfig();

    const { adType, elementType, placementId, creativeId } = useMemo(() => {
        const adType = 'Content';
        const elementType = isMultipleAds
            ? multiAdIndex === 0
                ? 'Split Left'
                : 'Split Right'
            : undefined;

        const placementId = isMultipleAds
            ? `${originalPlacementId}_split_${multiAdIndex + 1}`
            : originalPlacementId;
        let creativeId = contentMarketing.id;

        if (contentMarketing.variantName) {
            creativeId += `_${contentMarketing.variantName}`;
        }

        return { adType, elementType, placementId, creativeId };
    }, [
        isMultipleAds,
        multiAdIndex,
        originalPlacementId,
        contentMarketing.id,
        contentMarketing.variantName,
    ]);

    const creativeClickObject: CreativeClickProps = useMemo(
        () => ({
            creativeId,
            elementId: placementId,
            verticalName: config.vertical,
            subVertical: config.subvertical,
            targetId: contentMarketing.href,
            variantName: contentMarketing.variantName,
            provider: contentMarketing.sponsorData.name,
            adType,
            elementType,
            adFormat: isMultipleAds ? 'Split-view' : 'Single-view',
        }),
        [
            contentMarketing,
            creativeId,
            config.vertical,
            config.subvertical,
            adType,
            elementType,
            isMultipleAds,
            placementId,
        ],
    );

    useEffect(() => {
        if (
            !pulseInitiated &&
            document &&
            placementId &&
            contentMarketing &&
            creativeId
        ) {
            if (contentMarketing.videoUrl) {
                logVideoLoad(
                    creativeId,
                    config.vertical,
                    config.subvertical,
                    0,
                    0,
                );
            }
            logCreativeLoad({
                ...creativeClickObject,
            });

            setPulseInitiated(true);
        }
    }, [
        placementId,
        pulseInitiated,
        creativeId,
        contentMarketing,
        creativeClickObject,
        config.vertical,
        config.subvertical,
    ]);

    return creativeClickObject;
};
type TContentMarketingElement = {
    placementId: string;
    multiAdIndex?: number;
    contentMarketing: TImprovedContentMarketing;
    children: React.ReactNode;
    isMultipleAds?: boolean;
};

export const ContentMarketingElement = ({
    placementId,
    contentMarketing,
    children,
    isMultipleAds = false,
    multiAdIndex = undefined,
}: TContentMarketingElement) => {
    const creativeClickObject = usePulseLoadAndClick({
        placementId,
        contentMarketing,
        isMultipleAds,
        multiAdIndex,
    });

    const id = creativeClickObject?.elementId || placementId;

    const handleClickEvent = (e) => {
        if (contentMarketing?.videoUrl) {
            if (e.target.id === `cm-title-${contentMarketing.id}`) {
                // Call your function here
                logCreativeClick(creativeClickObject);
            }
        } else {
            logCreativeClick(creativeClickObject);
        }
    };

    return (
        <article
            onClick={handleClickEvent}
            className={isMultipleAds ? 'w-full h-full' : 'col-span-full'}
            id={id}
        >
            {children}
        </article>
    );
};

/**
 *
 * @param children
 * @param placementId
 * @param contentMarketingList
 * @param containerClassName
 * @param flexGap
 * @constructor
 * @return a list of elements with a render props which returns contentMarketing and index
 */
export const MultiWrapper = ({
    children,
    placementId,
    contentMarketingList,
    containerClassName = 'col-span-full',
    flexGap = 'gap-8',
}) => {
    const { deviceType, vertical } = getConfig();

    const carouselStyle =
        vertical === 'realestate'
            ? {
                  width: `calc(100vw)`,
                  marginLeft: -22,
                  paddingLeft: 16,
                  paddingRight: 22,
              }
            : {
                  width: 'calc(100vw + 16px)',
                  marginLeft: -32,
                  paddingLeft: 32,
                  paddingRight: 32,
              };
    if (deviceType === 'mobile') {
        return (
            <div className={containerClassName}>
                {Array.isArray(contentMarketingList) && (
                    <SponsorHeader contentMarketing={contentMarketingList[0]} />
                )}
                <div
                    className={'cm-carousel-container flex flex-row' + flexGap}
                    style={carouselStyle}
                >
                    {contentMarketingList.map(
                        (
                            contentMarketing: TImprovedContentMarketing,
                            i: number,
                        ) => (
                            <div
                                key={i}
                                className={
                                    'cm-carousel-child flex-auto w-2/4 m-4'
                                }
                            >
                                <div className={'h-full'}>
                                    <ContentMarketingElement
                                        key={contentMarketing.id}
                                        placementId={placementId}
                                        contentMarketing={contentMarketing}
                                        multiAdIndex={i}
                                        isMultipleAds={true}
                                    >
                                        <div
                                            data-testid={'cm-carousel-block'}
                                            className={'flex flex-col h-full'}
                                            style={{ width: '70vw' }}
                                        >
                                            {children(contentMarketing, i)}
                                        </div>
                                    </ContentMarketingElement>
                                </div>
                            </div>
                        ),
                    )}
                </div>
            </div>
        );
    }
    return (
        <div className={containerClassName}>
            {Array.isArray(contentMarketingList) && (
                <SponsorHeader contentMarketing={contentMarketingList[0]} />
            )}
            <div className={'flex flex-row ' + flexGap}>
                {contentMarketingList.map(
                    (
                        contentMarketing: TImprovedContentMarketing,
                        i: number,
                    ) => (
                        <div key={i} className={'flex-auto w-2/4'}>
                            <div className={'h-full'}>
                                <ContentMarketingElement
                                    key={contentMarketing.id}
                                    placementId={placementId}
                                    contentMarketing={contentMarketing}
                                    multiAdIndex={i}
                                    isMultipleAds={true}
                                >
                                    {children(contentMarketing, i)}
                                </ContentMarketingElement>
                            </div>
                        </div>
                    ),
                )}
            </div>
        </div>
    );
};
